import { useState, useCallback, lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { useCurrentUser, capsForRole, roleLabel, ROLE_ORDER, type Role, type Caps, type Me } from "./auth";
import { C, F } from "./theme";
import { LoadingScreen, ErrorScreen, RolePill, Btn } from "./components/ui";
import { usePlanning, type Planning } from "./pages/planning/usePlanning";
import { DeptFilter } from "./pages/planning/DeptFilter";

// Heavy views are code-split: each downloads its own chunk on first visit
// (then served from the year-long immutable /assets cache). The shell, auth,
// theme, the planning hook and DeptFilter stay eager — they're needed up front.
// LinePanel is a default export; the rest are named, hence the `.then` remap.
const LinePanel = lazy(() => import("./pages/LinePanel"));
const TrainGraph = lazy(() => import("./pages/TrainGraph").then((m) => ({ default: m.TrainGraph })));
const TTBuilder = lazy(() => import("./pages/ttbuilder/TTBuilder").then((m) => ({ default: m.TTBuilder })));
const SnapshotView = lazy(() => import("./pages/planning/SnapshotView").then((m) => ({ default: m.SnapshotView })));
const MatrixView = lazy(() => import("./pages/planning/MatrixView").then((m) => ({ default: m.MatrixView })));
const CalendarView = lazy(() => import("./pages/planning/CalendarView").then((m) => ({ default: m.CalendarView })));
const DayDetail = lazy(() => import("./pages/planning/DayDetail").then((m) => ({ default: m.DayDetail })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const RequestsPanel = lazy(() => import("./pages/RequestsPanel").then((m) => ({ default: m.RequestsPanel })));
const PossessionOrdersPanel = lazy(() => import("./pages/PossessionOrdersPanel").then((m) => ({ default: m.PossessionOrdersPanel })));
const ActionCalendar = lazy(() => import("./pages/action/ActionCalendar").then((m) => ({ default: m.ActionCalendar })));

interface TabDef {
  key: string;
  label: string;
  path: string;
  phase: string;
  blurb: string;
  allowed: (c: Caps) => boolean;
}

// Line is gated on lineView independently of the planning tabs (otherView).
// Users & Access is admin-only. (SSO-AND-ROLES §4.)
const TABS: TabDef[] = [
  {
    key: "line", label: "Line", path: "/line", phase: "Phase 1–4",
    blurb: "The live line map, shunt planner, Special Traffic Notices (STN) and possessions. The operational heart of the tool.",
    allowed: (c) => c.lineView,
  },
  {
    key: "graph", label: "Train graph", path: "/graph", phase: "Roadmap",
    blurb: "The time–distance string diagram — the whole day's running on one chart; crossings, holds and possessions at a glance.",
    allowed: (c) => c.lineView,
  },
  {
    key: "ttb", label: "TTBuilder", path: "/ttbuilder", phase: "Phase 8",
    blurb: "The timetable designer — build the season's working timetable, season calendar, review/approval and publish lifecycle. Admin only.",
    allowed: (c) => c.admin,
  },
  {
    key: "snapshot", label: "Snapshot", path: "/snapshot", phase: "Phase 5",
    blurb: "The day strip and inline day view — today's traction, sets and priority at a glance.",
    allowed: (c) => c.otherView,
  },
  {
    key: "matrix", label: "Matrix", path: "/matrix", phase: "Phase 5",
    blurb: "The day × department matrix — which locos and carriage sets are committed where, against the timetable.",
    allowed: (c) => c.otherView,
  },
  {
    key: "calendar", label: "Calendar", path: "/calendar", phase: "Phase 5",
    blurb: "Month grid and detail mode — summaries, timetable types and blockers across the running season.",
    allowed: (c) => c.otherView,
  },
  {
    key: "action", label: "NYMR Action Calendar", path: "/action", phase: "Phase 11",
    blurb: "Committed & proposed events laid against the running timetable — stakeholder comments, red alerts and Change Management Notices for executive sign-off.",
    allowed: (c) => c.otherView,
  },
  {
    key: "settings", label: "Settings", path: "/settings", phase: "Admin",
    blurb: "Users & Access, templates, folklore, runround defaults and the living audit trail.",
    allowed: (c) => c.admin,
  },
];

export default function App() {
  const { me, loading, error } = useCurrentUser();
  if (loading) return <LoadingScreen label="Signing in…" />;
  if (error) return <ErrorScreen message={error} />;
  if (!me) return <LoadingScreen label="Redirecting to sign-in…" />;
  return <AuthedApp me={me} />;
}

function AuthedApp({ me }: { me: Me }) {
  const [viewAs, setViewAs] = useState<Role | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [possOpen, setPossOpen] = useState(false);
  const flash = useCallback((m: string) => { setToast(m); window.setTimeout(() => setToast((t) => (t === m ? null : t)), 3200); }, []);

  const planning = usePlanning(me, me.caps.otherView, flash);

  const previewing = me.isSsoAdmin && viewAs !== null && viewAs !== me.role;
  const effCaps: Caps = previewing && viewAs ? capsForRole(viewAs) : me.caps;
  const visibleTabs = TABS.filter((t) => t.allowed(effCaps));
  const selectedDay = planning.days?.find((d) => d.date === planning.selectedDate) ?? null;

  // The dept filter + Requests + Possessions controls belong to the planning
  // views (Snapshot / Matrix / Calendar) — shown in context there, not globally.
  const loc = useLocation();
  const onPlanningView = ["/snapshot", "/matrix", "/calendar"].some((p) => loc.pathname.startsWith(p));

  return (
    <div style={{ minHeight: "100vh", background: C.paper, display: "flex", flexDirection: "column" }}>
      <Topbar me={me} viewAs={viewAs ?? me.role} onViewAs={(r) => setViewAs(r === me.role ? null : r)} tabs={visibleTabs}
        filterControl={onPlanningView && (effCaps.otherView || effCaps.lineView) ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {effCaps.otherView && <DeptFilter filter={planning.filter} setFilter={planning.setFilter} myDept={me.dept} />}
            {effCaps.otherView && <button type="button" onClick={() => setRequestsOpen(true)} title="Movement & possession requests — raise & view" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 999, cursor: "pointer", border: `1px solid ${C.hairline}`, background: C.card, fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: C.brassDk }}>Requests</button>}
            <button type="button" onClick={() => setPossOpen(true)} title="Engineering possession orders — raise, issue, download & track acknowledgement" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 999, cursor: "pointer", border: `1px solid ${C.hairline}`, background: C.card, fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: C.brassDk }}>Possessions</button>
          </div>
        ) : null} />
      {previewing && viewAs && (
        <PreviewBanner role={viewAs} realRole={me.role} onExit={() => setViewAs(null)} />
      )}
      <main style={{ flex: 1, minHeight: 0 }}>
        <Suspense fallback={<LoadingScreen label="Loading…" />}>
        <Routes>
          {visibleTabs.map((t) => (
            <Route
              key={t.key}
              path={t.path}
              element={
                t.key === "line" ? <LinePanel canEdit={effCaps.lineEdit} me={me} />
                  : t.key === "graph" ? <TrainGraph flash={flash} canEdit={effCaps.lineEdit} />
                  : t.key === "ttb" ? <TTBuilder flash={flash} />
                    : t.key === "settings" ? <SettingsPage me={me} flash={flash} />
                      : t.key === "action" ? <ActionCalendar me={me} flash={flash} canEdit={effCaps.otherEdit} isAdmin={effCaps.admin} />
                        : <PlanningPane view={t.key} planning={planning} isEditor={effCaps.otherEdit} me={me} />
              }
            />
          ))}
          {/* Any path the effective role can't see falls through to the first allowed tab. */}
          <Route path="*" element={<Navigate to={visibleTabs[0]?.path ?? "/line"} replace />} />
        </Routes>
        </Suspense>
      </main>

      {/* Planning day-detail drawer (Snapshot / Matrix / Calendar → open day) */}
      {selectedDay && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9990, background: "rgba(43,32,20,0.45)", display: "flex", justifyContent: "flex-end" }}>
          <div onClick={planning.closeDay} style={{ position: "absolute", inset: 0 }} />
          <div style={{ position: "relative", width: 1340, maxWidth: "94vw", height: "100%", background: C.paperSoft, boxShadow: "-20px 0 50px rgba(43,32,20,0.3)", animation: "bpslide .2s ease" }}>
            <Suspense fallback={null}>
              <DayDetail day={selectedDay} isEditor={effCaps.otherEdit} onAck={planning.ack} onAddNote={planning.addNote} onEditNote={planning.editNote} onDeleteNote={planning.deleteNote} onClose={planning.closeDay} filter={planning.filter} me={me} myKey={planning.myKey} />
            </Suspense>
          </div>
        </div>
      )}

      {requestsOpen && (
        <Suspense fallback={null}>
          <RequestsPanel me={me} canPlan={effCaps.lineEdit} onClose={() => setRequestsOpen(false)} flash={flash} />
        </Suspense>
      )}

      {possOpen && (
        <Suspense fallback={null}>
          <PossessionOrdersPanel me={me} onClose={() => setPossOpen(false)} flash={flash} />
        </Suspense>
      )}

      {toast && (
        <div style={{ position: "fixed", left: "50%", bottom: 22, transform: "translateX(-50%)", zIndex: 9999, background: C.ink, color: C.inkOnDark, fontFamily: F.body, fontSize: 13, fontWeight: 700, padding: "10px 18px", borderRadius: 10, boxShadow: C.shadowL, maxWidth: "90vw" }}>{toast}</div>
      )}
    </div>
  );
}

function PlanningPane({ view, planning, isEditor, me }: { view: string; planning: Planning; isEditor: boolean; me: Me }) {
  if (planning.loading) return <div style={{ padding: 48, textAlign: "center", fontFamily: F.body, fontSize: 14, color: C.inkMute }}>Loading the plan…</div>;
  if (planning.error) return <div style={{ padding: 48, textAlign: "center", fontFamily: F.body, fontSize: 14, color: C.signalDk }}>{planning.error}</div>;
  if (!planning.days) return null;
  const common = { days: planning.days, today: planning.today, filter: planning.filter, onSelect: planning.openDay };
  if (view === "matrix") return <MatrixView {...common} />;
  if (view === "calendar") return <CalendarView {...common} />;
  return <SnapshotView {...common} isEditor={isEditor} me={me} myKey={planning.myKey} onAck={planning.ack} onAddNote={planning.addNote} />;
}

function Topbar({
  me, viewAs, onViewAs, tabs, filterControl,
}: {
  me: Me;
  viewAs: Role;
  onViewAs: (r: Role) => void;
  tabs: TabDef[];
  filterControl: ReactNode;
}) {
  const loc = useLocation();
  const activeKey = tabs.find((t) => loc.pathname.startsWith(t.path))?.key ?? tabs[0]?.key;

  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 20, background: C.paperSoft,
      borderBottom: `1px solid ${C.hairline}`, boxShadow: C.shadowS,
      borderTop: `3px solid ${C.brass}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 22, padding: "10px 22px", flexWrap: "wrap",
      }}>
        {/* Wordmark */}
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.05, marginRight: 6 }}>
          <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 700, color: C.brassDk, letterSpacing: 0.3 }}>
            The Big Plan
          </span>
          <span style={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.inkMute, letterSpacing: 1.5, textTransform: "uppercase" }}>
            NYMR · Movement Planning
          </span>
        </div>

        {/* Tab nav */}
        <nav style={{ display: "flex", gap: 4, flex: 1 }}>
          {tabs.map((t) => {
            const active = t.key === activeKey;
            return (
              <Link key={t.key} to={t.path} style={{
                textDecoration: "none", fontFamily: F.body, fontSize: 14, fontWeight: 700,
                color: active ? C.brassDk : C.inkSoft, padding: "8px 12px 9px",
                borderBottom: `2.5px solid ${active ? C.brass : "transparent"}`,
                whiteSpace: "nowrap",
              }}>
                {t.label}
              </Link>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {filterControl}

          {me.isSsoAdmin && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }} title="Preview the app as another role (admin only). Never changes server enforcement.">
              <span style={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.inkMute, letterSpacing: 0.6, textTransform: "uppercase" }}>View as</span>
              <select
                value={viewAs}
                onChange={(e) => onViewAs(e.target.value as Role)}
                style={{
                  fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.inkSoft,
                  background: C.card, border: `1px solid ${C.hairline}`, borderRadius: 8, padding: "6px 8px", cursor: "pointer",
                }}
              >
                {ROLE_ORDER.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </label>
          )}

          {/* User chip */}
          <div style={{
            display: "flex", alignItems: "center", gap: 9, paddingLeft: 12, borderLeft: `1px solid ${C.hairline}`,
          }}>
            <Avatar name={me.name} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.15 }}>
              <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.ink }}>{me.name}</span>
              <RolePill role={me.role} />
            </div>
            <a href="/logout" title="Sign out" style={{
              fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkMute, textDecoration: "none", paddingLeft: 4,
            }}>Sign out</a>
          </div>
        </div>
      </div>
    </header>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? "").join("") || "?";
  return (
    <div style={{
      width: 34, height: 34, borderRadius: "50%", background: C.brassBg, border: `1px solid rgba(167,117,28,0.30)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: F.serif, fontSize: 14, fontWeight: 700, color: C.brassDk,
    }}>{initials}</div>
  );
}

function PreviewBanner({ role, realRole, onExit }: { role: Role; realRole: Role; onExit: () => void }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      background: C.brassBg, borderBottom: `1px solid rgba(167,117,28,0.30)`,
      padding: "7px 16px", fontFamily: F.body, fontSize: 13, color: C.brassDk,
    }}>
      <span style={{ fontWeight: 800 }}>
        Previewing as {roleLabel(role)}
      </span>
      <span style={{ color: C.inkSoft }}>
        — your role is {roleLabel(realRole)}. This only changes your view; the server still enforces your real permissions.
      </span>
      <Btn variant="ghost" onClick={onExit} style={{ padding: "4px 10px", color: C.brassDk }}>Exit preview</Btn>
    </div>
  );
}
